import {
  Autocomplete,
  type AutocompleteProps,
  Checkbox,
  TextField,
  type TextFieldVariants,
  createFilterOptions,
} from "@mui/material"
import { type ElementType, type ReactNode, forwardRef, useMemo } from "react"
import { ChevronDown } from "react-feather"
import { isPlainObject } from "remeda"
import type { RequireExactlyOne } from "type-fest"

/**
 * Made for backend lists format
 * Some use `code` others `value`
 */
export type SearchOption<T> = RequireExactlyOne<
  {
    formattedLabel?: ReactNode
    label: string
    value: T
    code: T
  },
  "value" | "code"
>

export interface SearchAutocompleteProps<Option>
  extends Omit<
    AutocompleteProps<SearchOption<Option>, boolean, boolean, false>,
    "renderInput" | "options"
  > {
  id?: string
  options: SearchOption<Option>[]
  multiple?: boolean
  label?: string
  required?: boolean
  keepOriginalOrder?: boolean
  size?: "small" | "medium"
  variant?: TextFieldVariants
  placeholder?: string
  error?: boolean
  helperText?: string
  TextFieldComponent?: ElementType
}

// biome-ignore lint/suspicious/noExplicitAny: forwardRef does not support generic type parameters
export const SearchAutocomplete = forwardRef<
  HTMLDivElement,
  SearchAutocompleteProps<any>
>(
  (
    {
      options,
      multiple = false,
      keepOriginalOrder = false,
      value,
      placeholder,
      label,
      required = false,
      variant = "outlined",
      error,
      helperText,
      TextFieldComponent = TextField,
      ...autocompleteProps
    },
    ref,
  ) => {
    const filter = useMemo(
      () => createFilterOptions<SearchOption<string | number>>(),
      [],
    )
    const sortedOptions = useMemo(
      () =>
        keepOriginalOrder
          ? options
          : [...options].sort((a, b) => a.label.localeCompare(b.label)),
      [options, keepOriginalOrder],
    )

    const parsedValue = parseFieldValue(value, multiple)

    const isValue = Array.isArray(value) ? value.length > 0 : !!value

    return (
      <Autocomplete<SearchOption<string | number>, typeof multiple, boolean, false>
        ref={ref}
        // biome-ignore lint/suspicious/noExplicitAny: value type depends on multiple flag
        value={parsedValue as any}
        filterOptions={filter}
        options={sortedOptions}
        getOptionLabel={(option) =>
          sortedOptions.find((opt) => getOptionValue(opt) === getOptionValue(option))
            ?.label || ""
        }
        renderOption={({ key, id, ...restProps }, option, { selected }) => (
          <li key={key + id} {...restProps}>
            {!!multiple && <Checkbox checked={selected} />}
            {option.formattedLabel || option.label}
          </li>
        )}
        ChipProps={{ variant: "outlined", color: "primary" }}
        isOptionEqualToValue={(option, currValue) =>
          getOptionValue(option) === getOptionValue(currValue)
        }
        selectOnFocus
        clearOnBlur
        multiple={multiple}
        popupIcon={<ChevronDown />}
        disableCloseOnSelect={multiple}
        noOptionsText="Aucun résultat"
        renderInput={(params) => (
          <TextFieldComponent
            {...params}
            label={label}
            error={error}
            helperText={helperText}
            required={required}
            variant={variant}
            placeholder={!isValue ? placeholder : undefined}
          />
        )}
        {...autocompleteProps}
      />
    )
  },
)

const parseFieldValue = <T,>(value: T, isMultiple: boolean) => {
  if (isMultiple) {
    if (Array.isArray(value)) {
      return value
    }
    return value ? [value] : []
  }
  return value ?? ""
}

/** Factorize option.value / option.code access */
export const getOptionValue = <T,>(option: Partial<SearchOption<T>> | T) => {
  if (isPlainObject(option)) {
    return option.value || option.code
  }
  return option
}
